// ===========================================
// HOOK UNIVERSAL PARA SQLEXEC
// - Backend maneja caché HTTP
// - Auto-refresh inteligente
// - Throttling automático
// - Zero hardcode
// ===========================================
import { useState, useEffect, useCallback, useRef } from 'react';
import { secureFetch } from '../utils/authInterceptor';
import { toast } from "react-toastify";
import { resolvePlaceholders } from '../utils/envioEmails';

const useSqlexecData = (componentForm, options = {}) => {
  const { 
  action = 'read', 
  autoFetch = true,
  enableFocusRefresh = true,
  focusThresholdSeconds = 30,
  throttleMs = 500,
  parentKey,
  parentValue,
  parentRow,
  limit = 13,
  reactiveParentRefresh = false,
  initialFilters = [],
} = options;

  // ===========================================
  // NORMALIZAR CONTEXTO PADRE
  // ===========================================
  const normalizedParentValue =
    parentValue === undefined ||
    parentValue === null ||
    parentValue === -1
      ? null
      : parentValue;

  // ===========================================
  // ESTADOS
  // ===========================================
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [columnas, setColumnas] = useState([]);
  const [schema, setSchema] = useState(null);
  const [acciones, setAcciones] = useState(null);
  const [idsqlexec, setIdsqlexec] = useState(null); // identificador SQLEXEC real (fila de config)

  // ===========================================
  // REFS INTERNOS
  // ===========================================
  const hasFetchedRef = useRef(false);
  const lastRefreshTime = useRef(Date.now());
  const lastFocusTime = useRef(Date.now());
  const lastCallTime = useRef(0);
  const [nextCursor, setNextCursor] = useState(0);
  const [hasMore, setHasMore]       = useState(false);
  const cursorRef                   = useRef(0);
  const searchRef   = useRef('');
  const orderByRef  = useRef('');
  const orderDirRef = useRef('');
  const filtersRef  = useRef(initialFilters);

  // ===========================================
  // FETCH PRINCIPAL
  // ===========================================
  const fetchData = useCallback(async (bypassCache = false, resetData = false) => {
    if (!componentForm) return;

    // 🔒 BLOQUEO DURO: tab hijo sin padre seleccionado
    if (parentKey && normalizedParentValue === null) {
      setData([]);
      setColumnas([]);
      setSchema(null);
      setAcciones(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      let response;

      // ===========================================
      // CASO 1: TAB HIJO (CON FILTRO)
      // ===========================================
      if (parentKey && normalizedParentValue !== null && action === 'read') {

        const filterValue =
          typeof normalizedParentValue === 'object'
            ? normalizedParentValue[parentKey]
            : normalizedParentValue;

        response = await secureFetch(
          '/api/sqlexec_generico/read-with-filter',
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              component_form: componentForm,
              filters: {
                [parentKey]: filterValue
              },
              parentRow
            })
          }
        );

      } 
      // ===========================================
      // CASO 2: TAB PADRE / HUÉRFANO
      // ===========================================
      else {
        const search   = searchRef.current   || '';
        const orderBy  = orderByRef.current  || '';
        const orderDir = orderDirRef.current || '';

        const filters = Array.isArray(filtersRef.current) ? filtersRef.current : [];
        const baseParams = `cursor=${cursorRef.current}&limit=${limit}&search=${encodeURIComponent(search)}&orderBy=${orderBy}&orderDir=${orderDir}&filters=${encodeURIComponent(JSON.stringify(filters))}`;

        const url = bypassCache
          ? `/api/sqlexec/${componentForm}/${action}?${baseParams}&_t=${Date.now()}`
          : `/api/sqlexec/${componentForm}/${action}?${baseParams}`;

        //console.log('🔵 URL fetch:', url); //muestra que endpoint se invoka.

        response = await secureFetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(bypassCache && {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            })
          }
        });
      }

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const json = await response.json();

      if (!json.success) {
        throw new Error(json.message || 'Error obteniendo datos');
      }

      const innerData = json.data || {};

      setData(prev => {
        // ==========================================
        // RESET TOTAL DATASET
        // ==========================================
        if (resetData) {
          return innerData.data || [];
        }
        const newData = innerData.data || [];
        const existingIds = new Set(
          prev.map(r => r[Object.keys(r)[0]])
        );
        const deduplicated = newData.filter(
          r => !existingIds.has(r[Object.keys(r)[0]])
        );
        return [...prev, ...deduplicated];
      });

      setColumnas(innerData.columnas   || []);
      setSchema(innerData.schema       || null);
      setAcciones(innerData.acciones   || null);
      setIdsqlexec(innerData.idsqlexec ?? null);
      setNextCursor(innerData.next_cursor || 0);
      setHasMore(innerData.has_more       || false);

      lastRefreshTime.current = Date.now();
      setLoading(false);

    } catch (err) {
      console.error('❌ Error en useSqlexecData:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [componentForm, action, parentKey, normalizedParentValue, parentRow, limit]);

  // ===========================================
  // REFRESH CON THROTTLING
  // ===========================================
  const refresh = useCallback((opts = {}) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime.current;
    if (timeSinceLastCall < throttleMs) return;
    lastCallTime.current = now;

    if (opts.orderBy !== undefined) {
      orderByRef.current  = opts.orderBy;
      orderDirRef.current = opts.orderDir || 'asc';
    }

    cursorRef.current = 0; // ← resetear cursor
    fetchData(true, true);
  }, [fetchData, throttleMs]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;
    cursorRef.current = nextCursor;
    fetchData(true);
  }, [hasMore, loading, nextCursor, fetchData]);

  const setSearch = useCallback((value) => {
    searchRef.current = value;
    cursorRef.current = 0;
    fetchData(true, true);
  }, [fetchData]);

  const setOrder = useCallback((field, dir) => {
    orderByRef.current  = field;
    orderDirRef.current = dir;
    cursorRef.current   = 0;
    fetchData(true, true);
  }, [fetchData]);

  const setFilters = useCallback((filtersArray) => {
    let safe = [];
    try {
      safe = Array.isArray(filtersArray) ? filtersArray : [];
      safe = safe.filter(f => f && typeof f.campo === 'string' && f.campo.trim());
    } catch { safe = []; }
    filtersRef.current = safe;
    cursorRef.current  = 0;
    fetchData(true, true);
  }, [fetchData]);

  // ===========================================
  // UTILIDAD: TIEMPO DESDE ÚLTIMO REFRESH
  // ===========================================
  const getTimeSinceRefresh = useCallback(() => {
    const seconds = Math.floor(
      (Date.now() - lastRefreshTime.current) / 1000
    );

    if (seconds < 60) return `hace ${seconds}s`;
    if (seconds < 3600) return `hace ${Math.floor(seconds / 60)}m`;
    return `hace ${Math.floor(seconds / 3600)}h`;
  }, []);

  // ===========================================
  // AUTO-FETCH INICIAL
  // ===========================================
  useEffect(() => {
    if (autoFetch && componentForm && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchData(true);
    }
  }, [autoFetch, componentForm, fetchData]);

  // ===========================================
  // REFRESH REACTIVO MASTER-DETAIL
  // SOLO PARA COMPONENTES QUE LO REQUIERAN
  // ===========================================
  useEffect(() => {
    if (!reactiveParentRefresh) return;
    if (!parentKey) return;
    if (normalizedParentValue === null) return;
    cursorRef.current = 0;
    fetchData(true, true);
  }, [reactiveParentRefresh, parentKey, normalizedParentValue, fetchData]);

  // ===========================================
  // EVENTO GLOBAL DE REFRESH
  // ===========================================
  useEffect(() => {
    const handler = (e) => {
      if (e.detail === componentForm.toLowerCase()) {
        const delay = Math.random() * 500;
        setTimeout(() => refresh(), delay);
      }
    };

    window.addEventListener('sqlexec:refresh', handler);
    return () =>
      window.removeEventListener('sqlexec:refresh', handler);
  }, [componentForm, refresh]);

  // ===========================================
  // AUTO-REFRESH AL RECUPERAR FOCO
  // ===========================================
  useEffect(() => {
    if (!enableFocusRefresh) return;

    const handleFocus = () => {
      const now = Date.now();
      const inactiveSeconds =
        (now - lastFocusTime.current) / 1000;

      if (inactiveSeconds > focusThresholdSeconds) {
        refresh();
      }

      lastFocusTime.current = now;
    };

    const handleBlur = () => {
      lastFocusTime.current = Date.now();
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [enableFocusRefresh, focusThresholdSeconds, refresh]);

  // ==========================================
  // EXPORTACIÓN — PDF / EXCEL
  // ==========================================
  const ejecutarExport = useCallback(async (opcion, id) => {

    if (!opcion?.idreporte) {
      toast.error('No hay reporte configurado para esta opción');
      return;
    }

    // ==========================================
    // FORMATO NORMALIZADO -- opcion.formato explicito tiene prioridad
    // (necesario cuando el key de la accion no sigue la convencion
    // export_<formato>, ej. varias acciones PDF con labels/keys propios
    // en el mismo menu); si no viene, se deriva de opcion.key como antes
    // (export_pdf -> pdf, export_xml -> xml, etc.)
    // ==========================================
    const formato = opcion.formato || opcion.key?.replace(/^export_/, '') || 'pdf';
    const fmtLabel = opcion.label || formato.toUpperCase();

    const toastId = toast.info(
      `Generando ${fmtLabel}, un momento...`,
      { autoClose: false }
    );

    try {

      const token =
        sessionStorage.getItem('mapasac_access_token');

      // ==========================================
      // EXPORT ENGINE
      // ==========================================
      const response = await secureFetch(
        '/api/sqlexec_generico/export/reporte',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            idreporte: opcion.idreporte,
            id,
            formato
          })
        }
      );

      if (!response.ok) {

        const err =
          await response.json().catch(() => ({}));

        throw new Error(
          err.message ||
          `Error generando ${fmtLabel}`
        );
      }

      // ==========================================
      // PDF / XML: el backend devuelve la URL de vista previa
      // (/export/ver/<token>/<nombre>): la pestaña muestra el archivo y
      // "Guardar" propone su nombre. Los demas formatos siguen como blob.
      // ==========================================
      if (formato === 'pdf' || formato === 'xml') {
        const j = await response.json();
        if (!j?.data?.url) throw new Error(`Error generando ${fmtLabel}`);
        window.open(j.data.url);
      } else {
        const blob = await response.blob();

        const url = URL.createObjectURL(blob);

        window.open(url);

        // LIBERAR MEMORIA
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 5000);
      }

      // ==========================================
      // SUCCESS
      // ==========================================
      toast.update(toastId, {
        type: 'success',
        render: `${fmtLabel} generado correctamente..!`,
        autoClose: 1000
      });

    } catch (err) {

      console.error(
        `❌ ejecutarExport ${fmtLabel}:`,
        err
      );

      toast.update(toastId, {
        type: 'error',
        render: `${err.message} — revisa configuración`,
        autoClose: 4000
      });

    }

  }, []);

  const ejecutarEmailConfig = useCallback(async (accion, item, pkField) => {
    if (!accion?.idreporte) return null;
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const r = await secureFetch(
        `/api/sqlexec_generico/schema/${accion.component_form}?idreporte=${accion.idreporte}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const j = await r.json();
      const config = j?.data?.emailConfig || null;
      return {
        ...item,
        pk_id:       item[pkField] || '',
        folio_label: accion.folio_label || 'Folio',
        folio_id:    item[accion.folio_field] || '',
        titulo:      `${accion.articulo || 'el'} ${accion.folio_label || 'Documento'} #${item[accion.folio_field] || ''}`,
        asunto:  config ? resolvePlaceholders(config.asunto,  item) : '',
        mensaje: config ? resolvePlaceholders(config.mensaje, item) : '',
        para:    config?.para ? resolvePlaceholders(config.para, item) : '',
        cc:      config?.cc   ? resolvePlaceholders(config.cc,   item) : ''
      };
    } catch (err) {
      console.error('❌ ejecutarEmailConfig:', err);
      return { ...item };
    }
  }, []);

  // ===========================================
    // ACTUALIZACION Y REFRESH() A NIVEL REGISTRO
    // ===========================================
    const refreshRecord = useCallback(async (componentForm, recordId, idField) => {
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      // ← usar sqlexec_list con filtro por ID
      const r = await secureFetch(
        `/api/sqlexec/${componentForm}/read?cursor=0&limit=1&filters=${encodeURIComponent(JSON.stringify([{campo: idField, tipo: 'select', valor: recordId}]))}&_t=${Date.now()}`,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          } 
        }
      );
      const j = await r.json();
      if (j.success) {
        const registro = j.data?.data?.[0];
        if (registro) {
          setData(prev => prev.map(row => 
            String(row[idField]) === String(recordId)
              ? { ...row, ...registro }
              : row
          ));
        }
      }
    } catch (err) {
      console.error('❌ refreshRecord:', err);
    }
  }, []);

  // ===========================================
  // API PÚBLICA
  // ===========================================
  return { 
    data, loading, error, columnas, schema, acciones, idsqlexec, refresh, loadMore,
    hasMore, nextCursor, setSearch, setOrder, setFilters, setData,
    refreshRecord, ejecutarExport, ejecutarEmailConfig, 
    getTimeSinceRefresh, lastRefreshTime: lastRefreshTime.current
  };
};

export default useSqlexecData;
